import { computed, ref, type Ref } from 'vue';
import { CustomFieldEntity, CustomFieldStudentAccess, CustomFieldType } from '@/types/composition-functions';
import { useCustomFields } from '@/composables/queries/useCustomFields';
import { useStudentProfileFieldSettings } from '@/composables/queries/useStudentProfileFieldSettings';
import { useCustomFieldMutations } from '@/composables/mutations/useCustomFieldMutations';
import { useStudentProfileFormMutations } from '@/composables/mutations/useStudentProfileFormMutations';
import {
  builtInSettingFor, dataRequestKey, fieldsToOpen, studentFieldRows, type StudentFieldRow,
} from '@/utils/studentFields';

/** The data request wizard's list of fields students may fill in. */
const CHOICES_QUERY = 'StudentDataRequestFieldChoices';

/**
 * Every student field, built-in and custom, as one list, and the changes
 * Settings → Custom Fields and the data request wizard make to them: the
 * "Students" setting, Required, opening a field to students and adding a
 * new one. Needs Custom Fields → View to load; each change needs Update
 * (Create for a new field), and throws what the server refused.
 */
export function useStudentFields(options: { enabled?: Ref<boolean> } = {}) {
  const custom = useCustomFields({ entity: CustomFieldEntity.Student, includeInactive: true, enabled: options.enabled });
  const builtIn = useStudentProfileFieldSettings({ enabled: options.enabled });
  const rows = computed(() => studentFieldRows(custom.fields.value, builtIn.settings.value));

  const { create, update } = useCustomFieldMutations();
  const { saveSettings } = useStudentProfileFormMutations();
  /** The row being saved, so its controls wait. */
  const savingKey = ref<string | null>(null);

  async function save(
    row: StudentFieldRow,
    change: { access?: CustomFieldStudentAccess; required?: boolean },
    refetchChoices = false,
  ) {
    const refetch = refetchChoices ? { refetchQueries: [CHOICES_QUERY], awaitRefetchQueries: true } : {};
    if (row.field) {
      const input = {
        ...(change.access !== undefined && { student_access: change.access }),
        ...(change.required !== undefined && { is_required: change.required }),
      };
      await update.mutate({ id: row.field.id, input }, refetch);
    } else {
      const access = builtInSettingFor(change.access ?? row.access, change.required ?? row.required);
      await saveSettings.mutate({ fields: [{ key: row.key, access }] }, refetch);
    }
  }

  async function saving<T>(key: string, work: () => Promise<T>): Promise<T> {
    savingKey.value = key;
    try {
      return await work();
    } finally {
      savingKey.value = null;
    }
  }

  /** Staff only, Can see or Can fill in. A built-in field keeps Required while students fill it in. */
  function setAccess(row: StudentFieldRow, access: CustomFieldStudentAccess) {
    return saving(row.key, () => save(row, { access }));
  }

  function setRequired(row: StudentFieldRow, required: boolean) {
    return saving(row.key, () => save(row, { required }));
  }

  /**
   * "Let students fill this in": the field, and the state or LGA it pairs
   * with, become "Can fill in". Returns the rows changed.
   */
  function openToStudents(row: StudentFieldRow): Promise<StudentFieldRow[]> {
    const changes = fieldsToOpen(row, rows.value);
    return saving(row.key, async() => {
      for (const [index, change] of changes.entries()) {
        await save(change, { access: CustomFieldStudentAccess.Edit }, index === changes.length - 1);
      }
      return changes;
    });
  }

  /** A new short-text field students fill in. Returns its data request key. */
  function addDetail(label: string): Promise<string | null> {
    return saving('new', async() => {
      const result = await create.mutate(
        { input: { entity: CustomFieldEntity.Student, label: label.trim(), type: CustomFieldType.Text, student_access: CustomFieldStudentAccess.Edit } },
        { refetchQueries: ['CustomFieldDefinitions', CHOICES_QUERY], awaitRefetchQueries: true },
      );
      const field = result?.data?.createCustomField;
      return field ? dataRequestKey(field) : null;
    });
  }

  return {
    rows,
    loading: computed(() => custom.loading.value || builtIn.loading.value),
    error: computed(() => custom.error.value ?? builtIn.error.value ?? null),
    refetch: () => Promise.all([custom.refetch(), builtIn.refetch()]),
    savingKey,
    setAccess,
    setRequired,
    openToStudents,
    addDetail,
  };
}
